import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BRAND, frostedSx, primaryButtonSx, textButtonSx } from '../../styles/ui';
import { TERMS_TEXT } from '../../content/terms';

interface TermsAndConditionsProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  termsAccepted: boolean;
  // panel: fills the photo side on desktop; dialog: full screen on phones
  variant?: 'panel' | 'dialog';
}

// Renders the EULA with clause headings and indented sub-clauses
export const TermsText: React.FC = () => (
  <Typography
    variant="body2"
    component="div"
    sx={{
      color: BRAND.ink,
      '& .clause': { mt: 3, mb: 1, fontWeight: 'bold' },
      '& .subclause': { ml: 2, mt: 1, mb: 1 },
    }}
  >
    {TERMS_TEXT.split('\n').map((line, index) => {
      if (line.match(/^\d+\s/)) {
        return <div key={index} className="clause">{line}</div>;
      } else if (line.match(/^\d+\.\d+\s/)) {
        return <div key={index} className="subclause">{line}</div>;
      } else if (line.trim() === '') {
        return <div key={index} style={{ height: '1em' }} />;
      }
      return <div key={index}>{line}</div>;
    })}
  </Typography>
);

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ open, onClose, onAccept, termsAccepted, variant = 'panel' }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Check if content is scrollable
  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setShowScrollIndicator(scrollTop + clientHeight < scrollHeight - 10);
      }
    };

    if (open && contentRef.current) {
      checkScroll();
      contentRef.current.addEventListener('scroll', checkScroll);
    }

    return () => {
      if (contentRef.current) {
        contentRef.current.removeEventListener('scroll', checkScroll);
      }
    };
  }, [open]);

  const termsBody = <TermsText />;

  const acceptButton = (
    <Button
      onClick={onAccept}
      variant="contained"
      size="large"
      disabled={termsAccepted}
      sx={{ ...primaryButtonSx, minWidth: 150 }}
    >
      {termsAccepted ? 'Accepted' : 'I Accept'}
    </Button>
  );

  // Phones: the photo side (and so the panel) is hidden, so the terms open full screen
  if (variant === 'dialog') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        scroll="paper"
        aria-labelledby="terms-dialog-title"
        PaperProps={{ sx: { backgroundColor: 'var(--joan-bg)', color: BRAND.ink } }}
      >
        <DialogTitle id="terms-dialog-title" sx={{ color: BRAND.ink, pr: '56px', pt: 'calc(21px + env(safe-area-inset-top))' }}>
          Terms and Conditions
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 'calc(13px + env(safe-area-inset-top))', color: BRAND.ink }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: BRAND.line }}>
          <Typography variant="h6" component="h2" sx={{ color: BRAND.ink, mb: 2 }}>
            Ask Joan Mobile App EULA
          </Typography>
          {termsBody}
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 2, pb: 'calc(13px + env(safe-area-inset-bottom))', justifyContent: 'space-between' }}>
          <Button onClick={onClose} sx={textButtonSx}>Close</Button>
          {acceptButton}
        </DialogActions>
      </Dialog>
    );
  }

  if (!open) return null;

  // Desktop: fills the photo side of the registration screen
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        p: { md: 4, lg: 5 }
      }}
    >
      <Typography variant="h3" component="h2" sx={{ color: BRAND.ink, mb: 4 }}>
        Terms and Conditions
      </Typography>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...frostedSx,
          backgroundColor: BRAND.card,
          border: `1px solid ${BRAND.line}`,
          borderRadius: '13px',
          color: BRAND.ink
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 4,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: BRAND.line },
            '&::-webkit-scrollbar-thumb': {
              background: BRAND.lineStrong,
              borderRadius: '4px',
              '&:hover': { background: BRAND.faint },
            },
          }}
        >
          <Typography variant="h5" component="div" sx={{ mb: 3, color: BRAND.ink }}>
            Ask Joan Mobile App EULA
          </Typography>
          {termsBody}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 3, borderTop: `1px solid ${BRAND.line}` }}>
          {acceptButton}
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
